import React, { useState, useEffect, useRef } from "react";
import ReactDOM from 'react-dom';
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from '../../api/apiHelper';
import { invalidateApiCache, writeApiCache } from '../../utils/apiCache';
import Page from "../molecules/Page";
import { pluralizeTasks, pluralizeDays } from "../../utils/plural";
import { getBadgeStatus, getNextBadgeInfo } from "../../utils/badges";
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { FiBell, FiShield, FiCheck, FiChevronRight, FiList, FiZap, FiTarget, FiClock } from 'react-icons/fi';
import posthog from 'posthog-js'; // <--- IMPORT POSTHOG

// --- IMPORTY KONFIGURACJI GAMIFIKACJI ---
import { LEVEL_DEFINITIONS, getThresholdsForField } from "../../utils/gamificationConfig";

// --- IMPORTY DO POPUPU MAPY ---
import GoalAchievementPopup from "../molecules/GoalAchievementPopup";
import { getRoadmapData, DEFAULT_GOAL_BY_DOMAIN, checkMilestoneCompletion, sumSolvedInFields } from "../molecules/GoalRoadmapModal";

// --- IMPORTY CAPACITOR ---
import { PushNotifications } from "@capacitor/push-notifications";
import { registerForPushNotifications } from '../../services/pushNotifications';
import { isPlatform } from "@ionic/react";

// --- IMPORTY GRAFIK ---
import OrzelSesjaIcon from '../../img/icons/orzel_puchar.png';
import OrzelReceIcon from '../../img/icons/orzel_zenada.png';
import OrzelRece2Icon from '../../img/icons/orzel_rece.png';

// ========================================================================
// 0. HELPERY ANIMACJI (count-up, warianty stagger, confetti)
// ========================================================================
// Licznik narastający z easeOutCubic; respektuje reduced-motion przez `enabled`
const useCountUp = (from, to, { duration = 900, delay = 200, enabled = true } = {}) => {
    const [value, setValue] = useState(enabled ? from : to);
    useEffect(() => {
        if (!enabled || from === to) { setValue(to); return; }
        let raf;
        let startTs;
        const timeout = setTimeout(() => {
            const tick = (ts) => {
                if (!startTs) startTs = ts;
                const p = Math.min(1, (ts - startTs) / duration);
                const eased = 1 - Math.pow(1 - p, 3);
                setValue(Math.round(from + (to - from) * eased));
                if (p < 1) raf = requestAnimationFrame(tick);
            };
            raf = requestAnimationFrame(tick);
        }, delay);
        return () => { clearTimeout(timeout); if (raf) cancelAnimationFrame(raf); };
    }, [from, to, duration, delay, enabled]);
    return value;
};

const containerVariants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } }
};
const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
};
// Lista kart: sama jest itemem rodzica i staggeruje własne dzieci
const listVariants = {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut", staggerChildren: 0.07 } }
};

// ========================================================================
// 1. KOMPONENT MODALA (PUSH PROMPT)
// ========================================================================
const PushPromptModal = ({ isOpen, onClose, onConfirm }) => {
    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden relative p-6 flex flex-col items-center shadow-2xl animate-in zoom-in-95 duration-300">
                
                {/* Tło delikatne (Bez glow, tylko struktura) */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-gradient-to-b from-[#233855]/5 to-transparent pointer-events-none"></div>

                {/* 1. Nagłówek: Walidacja wysiłku */}
                <div className="relative z-10 text-center">

                    <h3 className="text-2xl font-apotek uppercase tracking-widest font-bold text-[#233855] leading-none mb-2">
                        Czy na pewno będziesz pamiętać?
                    </h3>
                    <p className="text-sm text-gray-600 text-center mb-8 max-w-[280px] leading-relaxed">
                        Nie pozwól, by przerwa w nauce zepsuła Twoje postępy.
                </p>
                </div>

                {/* 2. Mockup Powiadomienia (Czysty, Loss Aversion) */}
                <div className="relative w-full max-w-[260px] mb-8 z-10">
                   {/* Karta - Bez aury/glow, czysta biel */}
                   <div className="relative bg-white rounded-xl shadow-[0_4px_20px_-5px_rgba(35,56,85,0.15)] border border-gray-200 p-4 transform rotate-[-2deg] hover:rotate-0 transition-all duration-300">
                      <div className="flex justify-between items-center mb-2.5">
                         <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-[#233855] rounded-md flex items-center justify-center shadow-sm">
                               <FiShield className="w-3.5 h-3.5 text-white" />
                            </div>
                            <span className="text-[10px] font-bold text-[#E09F2E] uppercase tracking-wide">
                               OCHRONA WIEDZY
                            </span>
                         </div>
                         <span className="text-[10px] font-medium text-gray-400">jutro</span>
                      </div>
                      
                      <div className="space-y-1 text-left pl-1">
                         <p className="text-xs font-bold text-[#233855] leading-tight">
                            🧠 Utrwal wiedzę!
                         </p>
                         <p className="text-[11px] text-gray-600 leading-snug">
                            Aby Twój wysiłek nie poszedł na marne, zrób krótką powtórkę. 
                         </p>
                      </div>
                   </div>
                </div>

                {/* 3. Value Proposition (Focus na zachowaniu wyniku) */}
                {/* <div className="w-full relative z-10 mb-6 px-1">
                    <div className="flex items-start gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-[0_2px_12px_-4px_rgba(35,56,85,0.08)] text-left">
                        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#233855]/5 text-[#233855] border border-[#233855]/5 shrink-0">
                            <FiLock className="w-5 h-5" />
                        </div>
                        <div className="text-xs text-gray-500 leading-relaxed">
                            <span className="font-bold font-apotek uppercase tracking-wider text-[#233855] text-sm block mb-0.5">
                                Ochrona wiedzy
                            </span>
                            System przypomni o nauce tylko wtedy, gdy będzie to konieczne.

                        </div>
                    </div>
                </div> */}

                {/* 4. Przyciski */}
                <div className="w-full space-y-3 relative z-10">
                    <button
                        onClick={onConfirm}
                        className="group relative w-full flex justify-center items-center gap-2 rounded-xl bg-[#233855] px-6 py-4 text-sm font-apotek uppercase tracking-widest text-slate-100 shadow-lg hover:bg-[#2c456b] transition-all active:scale-[0.98]"
                    >
                        <FiBell className="w-4 h-4 group-hover:animate-[wiggle_1s_ease-in-out_infinite]" />
                        <span>Przypomnienia o nauce</span>
                    </button>
                    
                    <button
                        onClick={onClose}
                        className="w-full text-center py-2"
                    >
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors border-b border-transparent hover:border-gray-300">
                            Nie, będę pamiętać o nauce samodzielnie
                        </span>
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

// ========================================================================
// 2. KOMPONENTY POMOCNICZE UI
// ========================================================================
const StatTile = ({ Icon, value, label }) => (
    <div className="flex items-center gap-2.5 bg-white rounded-xl p-2.5 border border-gray-100 shadow-sm">
        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-[#233855]">
            <Icon className="text-[#E09F2E]" size={16} />
        </div>
        <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 leading-none">{label}</p>
            <p className="text-sm font-bold text-gray-900 mt-0.5 leading-none">{value}</p>
        </div>
    </div>
);

const ProgressIndicator = ({ label, value, max, unit, colorClass }) => {
    const prefersReducedMotion = useReducedMotion();
    const pct = Math.min(100, (value / max) * 100);
    return (
        <div>
            <div className="flex justify-between items-center mb-1">
                <p className="text-xs font-semibold text-gray-600">{label}</p>
                <p className="text-xs font-bold text-gray-800">{`${value} / ${max} ${unit}`}</p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 shadow-inner overflow-hidden">
                <motion.div
                    className={`h-2.5 rounded-full ${colorClass}`}
                    initial={prefersReducedMotion ? { width: `${pct}%` } : { width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
                />
            </div>
        </div>
    );
};

const MotivationalHeader = ({ message, progress }) => (
    <div className={`p-2 mb-2 rounded-2xl text-center`}>
        <div className="flex justify-center items-center h-40 mb-2">
            {typeof message.icon === 'string' ? (
                <span className="text-4xl">{message.icon}</span>
            ) : (
                message.icon
            )}
        </div>
        <h2 className="text-xl font-bold text-gray-900 font-apotek uppercase tracking-wider">
            {message.title}
        </h2>
        <p className="text-sm text-gray-700 mt-1 mb-5 max-w-md mx-auto">{message.text}</p>
        <div className="space-y-4">
            {progress.dailyGoal && (
                <ProgressIndicator
                    label="Postęp celu dziennego"
                    value={progress.dailyGoal.solved}
                    max={progress.dailyGoal.goal}
                    unit={pluralizeTasks(progress.dailyGoal.goal)}
                    colorClass="bg-gradient-to-r from-yellow-300 to-[#F8AB3B]"
                />
            )}
            {progress.badge && progress.badge.remainingDays > 0 && (
                <ProgressIndicator
                    label={`${progress.badge.remainingDays} ${pluralizeDays(progress.badge.remainingDays)} do kolejnej odznaki!`}
                    value={progress.badge.currentStreak}
                    max={progress.badge.totalDays}
                    unit={pluralizeDays(progress.badge.totalDays)}
                    colorClass="bg-gradient-to-r from-blue-300 to-blue-500"
                />
            )}
        </div>
    </div>
);

// ========================================================================
// 3. LOGIKA LEVELI I MESSAGES
// ========================================================================

const getCurrentLevel = (elo, solvedTasks, fieldId) => {
    const thresholds = getThresholdsForField(fieldId);
    const currentLevelDef = [...LEVEL_DEFINITIONS].reverse().find((levelDef) => {
        const req = thresholds[levelDef.name];
        if (!req) return false;
        return elo >= req.elo && solvedTasks >= req.tasks;
    });
    return currentLevelDef || null; 
};

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Bank Wiadomości
const MESSAGES_ZERO_ATTEMPTED = [
    () => ({ title: "Chwila przerwy?", text: "Wygląda na to, że potrzebujesz odpoczynku. Wróć, kiedy będziesz gotów na pierwsze wyzwanie!", }),
    () => ({ title: "Gotowy na start?", text: "Rozwiąż choćby jedno zadanie, by uruchomić algorytm ELO i zobaczyć, jak dopasuje się do Ciebie.", }),
    () => ({ title: "Czekamy na Ciebie", text: "Pamiętaj, każde rozwiązane zadanie to krok bliżej celu. Jesteśmy tu, gdy będziesz gotów!", }),
];

const MESSAGES_ZERO_CORRECT_GUIDANCE = [
    ({ nickname }) => ({ title: "Aj! 0% celności", text: `Spokojnie, nawet mistrzowie tak zaczynali! Następnym razem zobacz, co Orzeł AI ma do powiedzenia.`, }),
    () => ({ title: "Ktoś tu klikał na ślepo?", text: "Chyba zabrakło analizy rozwiązań... Pamiętaj, błąd to najlepsza nauka. Spróbuj jeszcze raz.", }),
    () => ({ title: "Hmm, coś poszło nie tak...", text: "Zero zadań, zero stresu, zero % na matu... Stop! Spróbuj jeszcze raz, żeby po każdym błędzie analizować rozwiązanie!", }),
];

const MESSAGES_GOAL_NOT_MET = [
    (remaining, pluralizeTasks) => ({ title: "Jeszcze trochę!", text: `Dobra robota! Do osiągnięcia celu dziennego brakuje Ci już tylko ${remaining} ${pluralizeTasks(remaining)}. Dasz radę jeszcze dzisiaj!`, }),
    (remaining, pluralizeTasks) => ({ title: "Jesteś blisko!", text: `Nieźle! Jeszcze tylko ${remaining} ${pluralizeTasks(remaining)} i cel dzienny będzie Twój. Nie odpuszczaj!`, }),
    (remaining, pluralizeTasks) => ({ title: "Całkiem nieźle!", text: `Dobrze Ci idzie! Zostało ${remaining} ${pluralizeTasks(remaining)}, by obronić serię. Dokończysz teraz?`, }),
];

const MESSAGES_GOAL_JUST_MET = [
    (currentStreak, pluralizeDays) => ({ title: "Cel osiągnięty!", text: `Gratulacje! Udało Ci się zrealizować swój cel dzienny. Twoja seria rośnie w siłę!`, }),
    (currentStreak, pluralizeDays) => ({ title: "Brawo Ty!", text: `Doskonale! Cel dzienny zaliczony, a Twoja seria jest bezpieczna. Tak trzymaj!`, }),
    (currentStreak, pluralizeDays) => ({ title: "Zrobione!", text: `Gratulacje! Udało Ci się osiągnąć cel, a Twoja seria jest bezpieczna! Cały czas do przodu!`, }),
];

const MESSAGES_GOAL_OVERACHIEVED = [
    (overachieved, pluralizeTasks) => ({ title: "Idziesz jak burza!", text: `Niesamowite! Nie tylko udało Ci się osiągnąć cel, ale też zrobić ${overachieved} ${pluralizeTasks(overachieved)} ponad normę. Dobrze Ci idzie!`, }),
    (overachieved, pluralizeTasks) => ({ title: "Jesteś nie do zatrzymania!", text: `Wow! ${overachieved} ${pluralizeTasks(overachieved)} ponad cel! Twój sukces maturalny jest coraz bliżej. Gratulacje!`, }),
    (overachieved, pluralizeTasks) => ({ title: "Niesamowite! ", text: `Już ${overachieved} ${pluralizeTasks(overachieved)} ponad dzisiejszą normę. Kto Cię zatrzyma? Świetna robota!`, }),
];

// ========================================================================
// 4. GŁÓWNY KOMPONENT EndSessionStats
// ========================================================================

const EndSessionStats = () => {
    const navigate = useNavigate();
    
    // NOWY STAN NA MODAL
    const [showPushModal, setShowPushModal] = useState(false);
    
    // NOWY STAN NA POPUP MAPY
    const [roadmapPopupData, setRoadmapPopupData] = useState(null);

    // Stany lokalne na dane
    const [problemsSession, setProblemsSession] = useState(null);
    const [fieldMapping, setFieldMapping] = useState(null);
    const [userStatusBeforeSession, setUserStatusBeforeSession] = useState(null);
    const [fullUserStatsBeforeSession, setFullUserStatsBeforeSession] = useState(null);
    // Pelne dane dzialow sprzed sesji (elo + liczba rozwiazanych) — potrzebne do
    // kamieni rangowych. localStorage jest czyszczony zaraz po odczycie, wiec
    // trzymamy je w stanie.
    const [availableFieldsBefore, setAvailableFieldsBefore] = useState([]);
    const [sessionStartTime, setSessionStartTime] = useState(null);
    
    const dataLoadedRef = useRef(false);
    const prefersReducedMotion = useReducedMotion();

    useEffect(() => {
        if (dataLoadedRef.current) return;
        dataLoadedRef.current = true;

        let loadedData;
        const cachedData = JSON.parse(localStorage.getItem("endedSessionCache"));

        if (cachedData) {
            loadedData = cachedData;
        } else {
            const activeProblemsSession = JSON.parse(localStorage.getItem("problemsSession")) || [];
            
            if (activeProblemsSession.length === 0) {
                navigate("/app", { replace: true });
                return;
            }

            loadedData = {
                problemsSession: activeProblemsSession,
                userStatusBefore: JSON.parse(localStorage.getItem("userStatusBeforeSession")) || {
                    problems_daily_goal: 10,
                    problems_solved_today: 0,
                    current_streak: 0,
                    nickname: "Mistrzu",
                    problems_solved_total: 0 // Fallback
                },
                availableFields: JSON.parse(localStorage.getItem("available_fields")) || [],
                startTime: localStorage.getItem("sessionStartTime"),
                fullUserStats: JSON.parse(localStorage.getItem("fullUserStatsBeforeSession")) || {},
            };

            localStorage.setItem("endedSessionCache", JSON.stringify(loadedData));
            
            ["problemsSession", "userEloDict", "problemsData", "solveProblemState", "sessionStartTime", "fullUserStatsBeforeSession", "userStatusBeforeSession", "available_fields", "sessionStatus"].forEach(key => localStorage.removeItem(key));
        }

        setProblemsSession(loadedData.problemsSession);
        setUserStatusBeforeSession(loadedData.userStatusBefore);
        setSessionStartTime(loadedData.startTime);
        setFullUserStatsBeforeSession(loadedData.fullUserStats);
        setAvailableFieldsBefore(loadedData.availableFields || []);

        const mapping = loadedData.availableFields.reduce((acc, field) => {
            acc[field.field_id] = field.field_name;
            return acc;
        }, {});
        setFieldMapping(mapping);
        
    }, [navigate]);

    // --- NOWA LOGIKA: Sprawdzanie Milestone'a Mapy ---
    useEffect(() => {
        if (!problemsSession || !userStatusBeforeSession || !fullUserStatsBeforeSession) return;

        const checkRoadmapProgress = () => {
            // Roadmapa zalezy od aktywnego poziomu — PR ma wlasne kamienie milowe.
            const activeDomain = (() => {
                try { return JSON.parse(localStorage.getItem('userStatus'))?.active_domain || 'PP'; }
                catch { return 'PP'; }
            })();
            const roadmapData = getRoadmapData(activeDomain);
            const fallbackGoal = DEFAULT_GOAL_BY_DOMAIN[activeDomain] || "solid";
            const currentGoalId = activeDomain === 'PR'
                ? fallbackGoal
                : (localStorage.getItem('user_current_roadmap_goal') || fallbackGoal);
            const roadmap = roadmapData[currentGoalId.toUpperCase()]
                || roadmapData[fallbackGoal.toUpperCase()];
            
            // 1. Odtwórz stan PRZED sesją.
            // Kamienie liczymy z zadan AKTYWNEGO poziomu — problems_solved_total
            // jest globalne, wiec na PR zaliczaloby kamienie od zadan z podstawy.
            // `available_fields` backend zwraca juz przefiltrowane po domenie.
            // `available_fields` to jedyne zrodlo z KOMPLETEM danych dzialu
            // (user_field_elo + total_solved_per_field) i juz przefiltrowane po
            // aktywnej domenie. radar_chart niesie samo elo, przez co hasRank()
            // zawsze zwracal false i kamienie rangowe nigdy sie nie odpalaly.
            const fieldsBefore = availableFieldsBefore.map(f => ({
                field_id: f.field_id,
                user_field_elo: f.user_field_elo || 0,
                total_solved_per_field: f.total_solved_per_field || 0,
            }));
            const domainFieldIds = new Set(fieldsBefore.map(f => f.field_id));

            const totalProblemsBefore = fieldsBefore.length
                ? sumSolvedInFields(fieldsBefore)
                : (userStatusBeforeSession.problems_solved_total || 0);

            // 2. Stan PO sesji — nakladamy na kazdy dzial to, co zdarzylo sie w sesji.
            const inDomain = (p) => !domainFieldIds.size || domainFieldIds.has(p.field_id);
            const correctProblemsInSession = problemsSession.filter(
                (p) => p.is_solved && inDomain(p)
            ).length;
            const totalProblemsNow = totalProblemsBefore + correctProblemsInSession;

            const sessionByField = problemsSession.reduce((acc, p) => {
                const entry = acc[p.field_id] || { eloChange: 0, solved: 0 };
                entry.eloChange += p.elo_change || 0;
                if (p.is_solved) entry.solved += 1;
                acc[p.field_id] = entry;
                return acc;
            }, {});

            const fieldsNow = fieldsBefore.map(f => {
                const s = sessionByField[f.field_id] || { eloChange: 0, solved: 0 };
                return {
                    ...f,
                    user_field_elo: Math.max(0, f.user_field_elo + s.eloChange),
                    total_solved_per_field: f.total_solved_per_field + s.solved,
                };
            });

            // 3. Sprawdź Milestone'y
            let achievement = null;

            for (const milestone of roadmap.milestones) {
                const wasDone = checkMilestoneCompletion(milestone, totalProblemsBefore, fieldsBefore);
                const isDone = checkMilestoneCompletion(milestone, totalProblemsNow, fieldsNow);

                if (!wasDone && isDone) {
                    achievement = milestone;
                }
            }

            if (achievement) {
                // --- POSTHOG MILESTONE ---
                posthog.capture('milestone_unlocked', { 
                    milestone_id: achievement.id,
                    milestone_title: achievement.title
                });
                
                setTimeout(() => {
                    setRoadmapPopupData(achievement);
                }, 1000); 
            }
        };

        checkRoadmapProgress();
    }, [problemsSession, userStatusBeforeSession, fullUserStatsBeforeSession, availableFieldsBefore]);

    // Guard ładowania
    if (problemsSession === null || fieldMapping === null || userStatusBeforeSession === null || fullUserStatsBeforeSession === null) {
        return (
            <Page showNav={false}>
                <div className="text-center py-10"><p className="text-xl text-gray-600">Ładowanie statystyk...</p></div>
            </Page>
        );
    }

    // --- Logika Obliczeniowa ---
    const totalProblems = problemsSession.length;
    const correctProblemsInSession = problemsSession.filter((p) => p.is_solved).length;
    const dailyGoal = userStatusBeforeSession.problems_daily_goal;
    const totalSolvedToday = userStatusBeforeSession.problems_solved_today + correctProblemsInSession;
    const goalWasMetBefore = userStatusBeforeSession.problems_solved_today >= dailyGoal;
    const goalIsMetNow = totalSolvedToday >= dailyGoal;
    const currentStreak = goalIsMetNow ? (userStatusBeforeSession.current_streak + (goalWasMetBefore ? 0 : 1)) : userStatusBeforeSession.current_streak;
    const currentBadgeInfo = getBadgeStatus(currentStreak);
    const nickname = userStatusBeforeSession.nickname || "Matematyku";

    // --- Czas i Statystyki ---
    const sessionStartTimeParsed = sessionStartTime ? parseInt(sessionStartTime, 10) : null;
    const sessionEndTime = Math.floor(Date.now() / 1000); 
    const totalSessionSeconds = sessionStartTimeParsed ? Math.max(0, sessionEndTime - sessionStartTimeParsed) : 0;
    const hours = Math.floor(totalSessionSeconds / 3600);
    const minutes = Math.floor((totalSessionSeconds % 3600) / 60);
    const seconds = totalSessionSeconds % 60;
    const formattedTotalTime = [
        hours > 0 ? `${hours}h` : null,
        minutes > 0 ? `${minutes}m` : null,
        `${seconds}s`
    ].filter(Boolean).join(' ') || '0s';
    const successRate = totalProblems > 0 ? `${Math.round((correctProblemsInSession / totalProblems) * 100)}%` : '0%';
    
    // Grupowanie problemów po ID działu
    const categories = [...new Set(problemsSession.map((p) => p.field_id))];


    // --- Generowanie Wiadomości ---
    const getEndSessionMessage = () => {
        let iconSrc;
        let altText;

        if (goalIsMetNow) {
            iconSrc = OrzelSesjaIcon;
            altText = "Orzeł z pucharem - cel osiągnięty!";
        } else {
            if (correctProblemsInSession > 0) {
                iconSrc = OrzelRece2Icon; 
                altText = "Orzeł motywator - dobra próba, walcz dalej!";
            } else {
                iconSrc = OrzelReceIcon;
                altText = "Orzeł jest zawiedziony - spróbuj jeszcze raz!";
            }
        }

        const orzelIcon = <img src={iconSrc} alt={altText} className="w-40 h-40" />;
        let messageData;

        if (totalSolvedToday > dailyGoal) {
            const overachieved = totalSolvedToday - dailyGoal;
            messageData = pickRandom(MESSAGES_GOAL_OVERACHIEVED)(overachieved, pluralizeTasks);
        }
        else if (goalIsMetNow) { 
            messageData = pickRandom(MESSAGES_GOAL_JUST_MET)(currentStreak, pluralizeDays);
        }
        else { 
            if (totalProblems === 0) {
                messageData = pickRandom(MESSAGES_ZERO_ATTEMPTED)();
            }
            else if (correctProblemsInSession === 0 && totalProblems > 0) {
                messageData = pickRandom(MESSAGES_ZERO_CORRECT_GUIDANCE)({ nickname });
            }
            else {
                const remaining = dailyGoal - totalSolvedToday;
                messageData = pickRandom(MESSAGES_GOAL_NOT_MET)(remaining, pluralizeTasks);
            }
        }
        
        return { icon: orzelIcon, ...messageData };
    };

    const badgeProgress = getNextBadgeInfo(currentStreak);
    if (badgeProgress) {
        badgeProgress.remainingDays = badgeProgress.totalDays - badgeProgress.currentStreak;
    }

    const motivationalMessage = getEndSessionMessage();

    const getButtonLabel = () => {
        if (totalProblems === 0) return "BIORĘ SIĘ DO ROBOTY!";
        if (correctProblemsInSession === 0) return "NASTĘPNYM RAZEM SIĘ UDA!";
        if (totalSolvedToday > dailyGoal) return "UWIELBIAM ROBIĆ ZADANIA!";
        if (goalIsMetNow) return "DO ZOBACZENIA NASTEPNYM RAZEM!";
        return "SUPER!";
    };
    const progressData = {
        dailyGoal: { solved: totalSolvedToday, goal: dailyGoal },
        badge: badgeProgress
    };
    

    // ========================================================================
    // 5. OBSŁUGA NAWIGACJI I POWIADOMIEŃ (ZAAWANSOWANE ŚLEDZENIE PUSH)
    // ========================================================================

const handleGoToDashboard = async () => {
    if (isPlatform('capacitor') && goalIsMetNow) {
        try {
            const permStatus = await PushNotifications.checkPermissions();

            if (permStatus.receive !== 'granted') {
                const dismissed = localStorage.getItem('push_prompt_dismissed');
                if (dismissed) {
                    const hoursSince = (Date.now() - parseInt(dismissed)) / (1000 * 60 * 60);
                    if (hoursSince < 12) {
                        finalizeAndNavigate();
                        return;
                    }
                }

                // Pierwsza prośba jest w onboardingu; tu tylko druga szansa — i nie w trakcie
                // oferty powitalnej, żeby nie zabierać jej miejsca na koniec sesji.
                let promoTs = 0;
                try { promoTs = JSON.parse(localStorage.getItem('userStatus'))?.promo_offer_expires_at || 0; } catch (e) {}
                if (promoTs > Date.now() / 1000) {
                    finalizeAndNavigate();
                    return;
                }

                setShowPushModal(true);

                posthog.capture('push_onboarding_modal_viewed', {
                    context: 'end_session_success',
                    current_permission_status: permStatus.receive
                });
                return;
            }
        } catch (e) {
            console.error("Błąd checkPermissions:", e);
        }
    }

    finalizeAndNavigate();
};

    const handlePushConfirm = async () => {
        // 2. EVENT: User kliknął "PEWNIE!" w Twoim modalu (Intencja: TAK)
        posthog.capture('push_onboarding_modal_clicked', { 
            action: 'accepted',
            context: 'end_session_success'
        });

        setShowPushModal(false); 
        
        try {
            // Sprawdzamy status przed rejestracją
            const check = await PushNotifications.checkPermissions();

            if (check.receive === 'denied') {
                // 3a. EVENT: System zablokował (User ma wyłączone w ustawieniach)
                posthog.capture('push_native_permission_result', { status: 'denied_in_settings' });
                alert("Powiadomienia są zablokowane w ustawieniach telefonu. Musisz je włączyć ręcznie.");
            } 
            else {
                // Próba rejestracji (to wywoła systemowy popup "Czy zezwolić?")
                await registerForPushNotifications();
                
                // Sprawdzamy co się stało PO próbie rejestracji
                const finalCheck = await PushNotifications.checkPermissions();
                
                // 3b. EVENT: Wynik ostateczny (Granted vs Denied przez usera w systemie)
                posthog.capture('push_native_permission_result', { 
                    status: finalCheck.receive, // 'granted' lub 'denied'
                    flow: 'end_session'
                });
            }
        } catch (e) {
            console.error("Błąd w handlePushConfirm:", e);
            posthog.capture('push_error', { error: e.message });
        } finally {
            finalizeAndNavigate();
        }
    };

const handlePushDecline = () => {
    posthog.capture('push_onboarding_modal_clicked', { 
        action: 'declined',
        context: 'end_session_success'
    });
    localStorage.setItem('push_prompt_dismissed', Date.now().toString());
    setShowPushModal(false);
    finalizeAndNavigate();
};

    const finalizeAndNavigate = () => {
        const statsBeforeSession = fullUserStatsBeforeSession || {};
        const newlyAchievedLevels = [];

        const problemsByField = problemsSession.reduce((acc, p) => {
            acc[p.field_id] = acc[p.field_id] || [];
            acc[p.field_id].push(p);
            return acc;
        }, {});

        for (const fieldId in problemsByField) {
            const fieldProblems = problemsByField[fieldId];
            const fieldName = fieldMapping[fieldId] || `Dział ${fieldId}`;
            
            const oldStats = statsBeforeSession[fieldId] || { elo: 0, total_problems_solved: 0 };
            
            const oldLevel = getCurrentLevel(oldStats.elo, oldStats.total_problems_solved, fieldId);
            
            const eloChange = fieldProblems.reduce((sum, p) => sum + p.elo_change, 0);
            const solvedInSession = fieldProblems.filter(p => p.is_solved).length;
            
            const newElo = Math.max(0, oldStats.elo + eloChange);
            const newSolvedCount = (oldStats.total_problems_solved || 0) + solvedInSession;
            
            const newLevel = getCurrentLevel(newElo, newSolvedCount, fieldId);

            if (newLevel && (!oldLevel || newLevel.name !== oldLevel.name)) {
                newlyAchievedLevels.push({
                    fieldName: fieldName,
                    oldLevel: oldLevel,
                    newLevel: newLevel
                });
            }
        }

        if (newlyAchievedLevels.length > 0) {
            localStorage.setItem('newAchievementsToShow', JSON.stringify(newlyAchievedLevels));
        }

        localStorage.removeItem("endedSessionCache");
        invalidateApiCache('user_dashboard_data', true);
        api.get('user_stats_view/', { skipLoading: true }).then(r => writeApiCache('user_stats', r.data)).catch(() => {});
        api.get('fields_preferences/', { skipLoading: true }).then(r => writeApiCache('fields_preferences', r.data)).catch(() => {});
        api.get('surefire-atoms/', { skipLoading: true }).then(r => writeApiCache('surefire-atoms', r.data)).catch(() => {});
        api.get('exams/', { skipLoading: true }).then(r => writeApiCache('exams', r.data)).catch(() => {});
        navigate("/app");
    };


    // ========================================================================
    // 6. RENDEROWANIE GŁÓWNE
    // ========================================================================
    return (
        <Page showNav={false}>
            {/* WYSKAKUJĄCY POPUP MAPY SUKCESU */}
            {roadmapPopupData && (
                <GoalAchievementPopup
                    isOpen={!!roadmapPopupData}
                    onClose={() => setRoadmapPopupData(null)}
                    milestone={roadmapPopupData}
                />
            )}

            <div className="bg-white lg:bg-transparent flex-1 w-full">
            <div className="flex flex-col max-w-2xl mx-auto w-full flex-grow px-4 lg:px-0 pb-8">
                <motion.div
                    className="bg-white -mx-4 px-4 lg:mx-0 lg:rounded-[3rem] lg:shadow-[4px_4px_12px_rgba(0,0,0,0.30)] lg:border lg:border-gray-200 py-4 lg:py-6 lg:px-14 w-[calc(100%+2rem)] lg:w-full space-y-6 lg:mt-[8vh]"
                    variants={containerVariants}
                    initial={prefersReducedMotion ? false : "hidden"}
                    animate="show"
                >

                    <motion.div variants={itemVariants}>
                        <MotivationalHeader message={motivationalMessage} progress={progressData} />
                    </motion.div>

                    <motion.div className="flex justify-center my-4" variants={itemVariants}>
                        <div className="relative">
                            <img src={currentBadgeInfo.image} alt="Odznaka serii" className="w-24 h-auto" />
                            {currentStreak > 0 && (
                                <span
                                    className="absolute top-1/2 left-1/2 -translate-x-[55%] -translate-y-[50%] font-bold text-4xl font-apotek"
                                    style={{ color: currentBadgeInfo.badgeColor, textShadow: '0px 1px 3px rgba(0,0,0,0.3)' }}
                                >
                                    {currentStreak}
                                </span>
                            )}
                        </div>
                    </motion.div>

                    <motion.div className="grid grid-cols-2 gap-3" variants={itemVariants}>
                        <StatTile Icon={FiCheck} value={correctProblemsInSession} label="Rozwiązane" />
                        <StatTile Icon={FiList} value={totalProblems} label="Liczba prób" />
                        <StatTile Icon={FiTarget} value={successRate} label="Skuteczność" />
                        <StatTile Icon={FiClock} value={formattedTotalTime} label="Czas sesji" />
                    </motion.div>

                    <motion.div className="space-y-2 pt-2" variants={listVariants}>
                        {categories.map((categoryId, index) => {
                            const fieldStats = fullUserStatsBeforeSession[categoryId] || {};
                            const startElo = fieldStats.elo !== undefined ? fieldStats.elo : 0;
                            const startTotalSolved = fieldStats.total_problems_solved || 0;

                            return (
                                <motion.div key={index} variants={itemVariants}>
                                    <CategoryStatistics
                                        categoryId={categoryId}
                                        categoryName={fieldMapping[categoryId] || `Dział ID: ${categoryId}`}
                                        problems={problemsSession.filter((p) => p.field_id === categoryId)}
                                        startElo={startElo}
                                        startTotalSolved={startTotalSolved}
                                    />
                                </motion.div>
                            );
                        })}
                    </motion.div>

                    <motion.div className="pt-2 pb-2 flex justify-center" variants={itemVariants}>
                        <button
                            onClick={handleGoToDashboard}
                            className="animate-pulse-gold flex w-full lg:w-[25rem] justify-center rounded-lg bg-[#233855] px-3 py-3 text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] active:scale-[0.98] transition-all"
                        >
                            {getButtonLabel()}
                        </button>
                    </motion.div>
                </motion.div>
            </div>
            </div>

            <PushPromptModal
                isOpen={showPushModal} 
                onClose={handlePushDecline} 
                onConfirm={handlePushConfirm} 
            />

        </Page>
    );
};


// ========================================================================
// 7. KOMPONENT CategoryStatistics
// ========================================================================
const CategoryStatistics = ({ categoryId, categoryName, problems, startElo, startTotalSolved }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showTaskTooltip, setShowTaskTooltip] = useState(false);
    const navigate = useNavigate();

    const totalCategoryProblems = problems.length;
    const correctCategoryProblems = problems.filter((p) => p.is_solved).length;
    const categorySuccessRate = totalCategoryProblems > 0 ? `${((correctCategoryProblems / totalCategoryProblems) * 100).toFixed(0)}%` : "0%";
    const averageTime = totalCategoryProblems > 0
        ? (problems.reduce((sum, p) => sum + ((p.end_time || 0) - (p.start_time || 0)), 0) / totalCategoryProblems).toFixed(1)
        : "0.0";
    const formattedAvgTime = `${averageTime}s`;
    // Backend oddaje juz zmiane po podlodze 0, ale sesja lezaca w localStorage
    // moze pochodzic sprzed tej zmiany - klamp pilnuje, zeby licznik nie zjechal
    // na minus i zeby etykieta nie pokazala straty, ktorej baza nie zapisala.
    const rawEloChange = problems.reduce((sum, p) => sum + (p.elo_change || 0), 0);
    const endElo = Math.max(0, startElo + rawEloChange);
    const eloChange = endElo - startElo;
    const eloChangeLabel = eloChange > 0 ? `+${eloChange}` : `${eloChange}`;

    // Ranga i postęp — identyczna logika jak FieldRow w Training
    const thresholds = getThresholdsForField(categoryId);
    const rank = getCurrentLevel(startElo, startTotalSolved, categoryId);
    const nextRank = LEVEL_DEFINITIONS.find((l) => {
        const req = thresholds[l.name];
        return !(startElo >= req.elo && startTotalSolved >= req.tasks);
    });
    const nextReq = nextRank ? thresholds[nextRank.name] : null;
    const prevRankIndex = nextRank ? LEVEL_DEFINITIONS.indexOf(nextRank) - 1 : LEVEL_DEFINITIONS.length - 1;
    const prevReq = prevRankIndex >= 0 ? thresholds[LEVEL_DEFINITIONS[prevRankIndex].name] : { elo: 0, tasks: 0 };
    const eloProgress = nextReq
        ? Math.min(100, Math.max(0, Math.round(((startElo - prevReq.elo) / (nextReq.elo - prevReq.elo)) * 100)))
        : 100;

    // Stan PO sesji — do animacji liczenia ELO i wypełniania paska
    const prefersReducedMotion = useReducedMotion();
    const endProgress = nextReq
        ? Math.min(100, Math.max(0, Math.round(((endElo - prevReq.elo) / (nextReq.elo - prevReq.elo)) * 100)))
        : 100;
    const animatedElo = useCountUp(startElo, endElo, { enabled: !prefersReducedMotion, delay: 400 });

    const handleProblemClick = (probs, problem_index) => {
        navigate("/view-solved-problem", { state: { problems: probs, problem_index } });
    };

    return (
        <div className="rounded-xl border border-gray-200 shadow-[0_2px_8px_rgba(0,0,0,0.07)] overflow-hidden">
            <button
                type="button"
                className="w-full text-left p-3.5 bg-white hover:bg-gray-50/80 active:scale-[0.98] transition-all duration-150 flex items-center gap-3 group"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
            >
                {/* Ikona rangi */}
                <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center">
                    {rank ? (
                        <img src={rank.icon.achieved} alt={rank.name} className="w-8 h-8" />
                    ) : (
                        <img src={LEVEL_DEFINITIONS[0].icon.notAchieved} alt="Brak rangi" className="w-8 h-8 opacity-25" />
                    )}
                </div>

                {/* Nazwa + pasek */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="font-semibold text-sm text-[#233855] leading-snug truncate">
                            {categoryName}
                        </span>
                        <span className="flex-shrink-0 text-xs text-gray-400 font-medium flex items-center gap-1">
                            ELO {animatedElo}
                            {eloChange !== 0 && (
                                <motion.span
                                    className={`font-bold ${eloChange > 0 ? 'text-[#007D00]' : 'text-[#8A0000]'}`}
                                    initial={prefersReducedMotion ? false : { opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5, duration: 0.3 }}
                                >
                                    {eloChangeLabel}
                                </motion.span>
                            )}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <motion.div
                                className="h-full bg-[#233855] rounded-full"
                                initial={prefersReducedMotion ? { width: `${endProgress}%` } : { width: `${eloProgress}%` }}
                                animate={{ width: `${endProgress}%` }}
                                transition={{ duration: 0.9, ease: "easeOut", delay: 0.4 }}
                            />
                        </div>
                        <span className="flex-shrink-0 text-[0.6rem] text-gray-400">
                            {nextRank ? nextRank.name : "Max"}
                        </span>
                    </div>
                </div>

                {/* Chevron */}
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center transition-colors duration-150">
                    <FiChevronRight
                        className={`w-4 h-4 text-gray-400 group-hover:text-[#233855] transition-all duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                    />
                </div>
            </button>

            {isExpanded && (
                <div className="border-t border-gray-100 p-4 bg-gray-50/40">
                    <div className="grid grid-cols-2 gap-2.5">
                        <StatTile Icon={FiCheck} value={correctCategoryProblems} label="Rozwiązane" />
                        <StatTile Icon={FiZap} value={eloChangeLabel} label="Zmiana ELO" />
                        <StatTile Icon={FiTarget} value={categorySuccessRate} label="Skuteczność" />
                        <StatTile Icon={FiClock} value={formattedAvgTime} label="Śr. czas" />
                    </div>

                    {problems.length > 0 && (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                            <div className="flex items-center gap-1.5 mb-2">
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">zadania</p>
                                <div
                                    className="relative flex items-center"
                                    onMouseEnter={() => setShowTaskTooltip(true)}
                                    onMouseLeave={() => setShowTaskTooltip(false)}
                                >
                                    <AiOutlineQuestionCircle className="h-4 w-4 text-[#233855] cursor-pointer" />
                                    {showTaskTooltip && (
                                        <div className="absolute z-50 bottom-full left-0 mb-2 w-60 max-w-[calc(100vw-2rem)] rounded-lg bg-[#233855] p-4 text-sm text-left text-white shadow-lg">
                                            Tutaj możesz jeszcze raz przejrzeć zadania zrobione w obecnej sesji.
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {problems.map((problem, index) => (
                                    <div
                                        key={index}
                                        onClick={() => handleProblemClick(problems, index)}
                                        className={`w-7 h-7 rounded-full flex items-center justify-center text-[0.6rem] font-bold text-white cursor-pointer transition-transform hover:scale-110 ${problem.is_solved ? "bg-[#007D00]" : "bg-[#8A0000]"}`}
                                    >
                                        {index + 1}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EndSessionStats;